import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, StatusBadge } from '../components/UI';
import { api } from '../api';

export default function DashboardHome() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [analytics, setAnalytics] = useState(null);



  // Data loading
  useEffect(() => {
    const load = () => {
      api.getLeads().then(setLeads).catch(() => {});
      api.getAppointments().then(setAppointments).catch(() => {});
      api.getAnalytics().then(setAnalytics).catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const stats = [
    { label: "Active leads",  value: analytics?.totalLeads    ?? leads.length,       icon: "ti-users",      color: "#534AB7" },
    { label: "Appointments",  value: analytics?.totalAppts    ?? appointments.length, icon: "ti-calendar",   color: "#1D9E75" },
    { label: "Messages sent", value: analytics?.totalMessages ?? 0,                  icon: "ti-message",    color: "#378ADD" },
    { label: "Policies sold", value: analytics?.totalPolicies ?? 0,                  icon: "ti-file-check", color: "#D85A30" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.2)", padding: "20px", boxShadow: "0 0 20px rgba(83,74,183,0.05)", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,rgba(83,74,183,0.1),rgba(29,158,117,0.1))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={"ti " + s.icon} style={{ fontSize: 17, color: s.color }} aria-hidden="true" />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>

        {/* Recent leads */}
        <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Recent leads</span>
            </div>
            <button onClick={() => navigate('/dashboard/leads')} style={{ fontSize: 12, color: "#534AB7", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>View all →</button>
          </div>
          {leads.length === 0 && <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>No leads yet — add your first lead</div>}
          {leads.slice(0, 6).map(l => (
            <div key={l._id || l.id} onClick={() => navigate('/dashboard/leads')} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "0.5px solid var(--border)", cursor: "pointer", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <Avatar name={l.name} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{l.name}</div>
                <div style={{ fontSize: 11, color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.ai_note || "Awaiting first contact..."}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <StatusBadge status={l.status} />
                <span style={{ fontSize: 10, color: "var(--text3)" }}>{l.last_contact ? new Date(l.last_contact).toLocaleDateString() : "New"}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Upcoming appointments */}
        <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(29,158,117,0.3)", overflow: "hidden", boxShadow: "0 0 20px rgba(29,158,117,0.06)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(29,158,117,0.08),rgba(83,74,183,0.06))", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", boxShadow: "0 0 8px #1D9E75" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Upcoming</span>
            </div>
            <button onClick={() => navigate('/dashboard/appointments')} style={{ fontSize: 12, color: "#1D9E75", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>View all →</button>
          </div>
          {appointments.length === 0 && <div style={{ padding: "30px 16px", textAlign: "center", color: "var(--text3)", fontSize: 12 }}>No appointments yet</div>}
          {appointments.filter(a => new Date(a.datetime) >= new Date()).slice(0, 5).map(a => (
            <div key={a._id || a.id} style={{ padding: "12px 16px", borderBottom: "0.5px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{a.lead_name}</span>
                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: a.status === "confirmed" ? "rgba(29,158,117,0.1)" : "rgba(186,117,23,0.1)", color: a.status === "confirmed" ? "#0F6E56" : "#854F0B" }}>{a.status}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text2)" }}>{new Date(a.datetime).toLocaleString()} · {a.coverage || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
